import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { LayoutChangeEvent, Text, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import Animated, {
    runOnJS,
    useAnimatedReaction,
} from 'react-native-reanimated';
import { Edge, SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';

import { RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import debounce from 'lodash.debounce';

import { LANGUAGE_KEYS } from 'config/languageKeys.ts';
import gStyles from 'config/styles';
import { FALLBACK_MESSAGE_ID } from 'config/testID';

import { SCREEN } from 'router/screens';
import {
    ModularScreens,
    RootTemplates,
    RouteNavigationParams,
    TEMPLATE_TO_SENTRY_NAVIGATION,
    TEMPLATE_TO_SENTRY_RENDERING,
} from 'router/types';

import {
    selectAppState,
    selectLayoutConfig,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import {
    selectPlayerConfig,
    selectSpotlightVideoVisible,
} from 'store/player/selectors';
import { getTemplateview } from 'store/railsBuilder/actions';
import { RBParsedRail, RBTemplates } from 'store/railsBuilder/reducer.types';
import {
    selectMainMenuConfigRequestStatus,
    selectNavigationMenu,
    selectRBTemplateviewRequestStatus,
    selectTemplateviewConfigByName,
} from 'store/railsBuilder/selectors';
import { endSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';
import { ReduxStore } from 'store/types';
import {
    selectIsNonInteractiveUI,
    selectNPVRCapability,
} from 'store/user/selectors';
import { isCapabilityValid, requestFailure, requestSuccess } from 'store/utils';

import { ContentType } from 'services/data-manager/types';

import useAnimatedHeader from 'utils/hooks/useAnimatedHeader';
import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import useInteractionsFinished from 'utils/hooks/useInteractionsFinished';
import useSafeAreaEdges from 'utils/hooks/useSafeAreaEdges';
import useScreenFocused from 'utils/hooks/useScreenFocused';
import useStripeParantalControl from 'utils/hooks/useStripeParantalControl';
import { navigationRef } from 'utils/navigation/navigationUtils';
import { isPhone, isWeb } from 'utils/responsiveness';

import { windowHeight } from 'screens/Epg/utils/epg-config';
import { underlayingPlayerModalElementsDataSet } from 'screens/PlayerModal/hooks/useModalAccessibility.ts';
import Promotional from 'screens/Promotional';

import ScrollArrowButtons from 'components/CustomButtons/ScrollArrowButtons';
import GenericPinDialog from 'components/GenericPinDialog';
import { RefreshControl } from 'components/RefreshControl';
import Spinner from 'components/Spinner';
import Stripe from 'components/Stripe';

import styles from './styles';
import { RailLoadingStatus } from './types';

type Props = {
    route: RouteProp<RouteNavigationParams, ModularScreens>;
    navigation: StackNavigationProp<RouteNavigationParams, ModularScreens>;
};

enum PageStatus {
    IDLE = 'IDLE',
    LOADED = 'LOADED',
    EMPTY = 'EMPTY',
    FAILED = 'FAILED',
    RELOAD = 'RELOAD',
}

const {
    ERROR_EMPTY_TEMPLATE_RESPONSE,
    EMPTY_CONTENT_PAGES_GLOBAL,
    EMPTY_CONTENT_PAGES_NO_RECORDING_CAPABILITY,
    EMPTY_CONTENT_PAGES_RECORDING_BE_ERROR,
    ALERT_RECORDING_NOT_AVAILABLE_DESCRIPTION,
} = LANGUAGE_KEYS;

function ModularPage({
    route: {
        key,
        name,
        params: { id },
    },
    navigation,
}: Props) {
    const { visible: playerVisible } = useSelector(selectPlayerConfig);
    const spotlightPlayerVisible = useSelector(selectSpotlightVideoVisible);
    const interactionFinished = useInteractionsFinished();
    const { pageScrollPosition, onScroll } = useAnimatedHeader();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { screenFocused } = useScreenFocused();

    const appState = useSelector(selectAppState);
    const { background } = useSelector(selectLayoutConfig);
    const mainMenuConfigRequestStatus = useSelector(
        selectMainMenuConfigRequestStatus,
    );
    const tabs = useSelector(selectNavigationMenu);
    const isNonInteractive = useSelector(selectIsNonInteractiveUI);
    const templateviewRequestStatus = useSelector(
        selectRBTemplateviewRequestStatus(id),
    );
    const templateviewConfig = useSelector((state: ReduxStore) =>
        selectTemplateviewConfigByName(state, id),
    );
    const npvrCapability = useSelector(selectNPVRCapability);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const {
        showPinDialog,
        onShowPinDialog,
        onParentalContolDismiss,
        onParentalControlChecked,
        onSetParentalControlCheckedCallback,
    } = useStripeParantalControl();

    const [pageState, setPageState] = useState<PageStatus>(PageStatus.IDLE);
    const [railsLoadingStatus, setRailsLoadingStatus] = useState<
        Record<string, RailLoadingStatus> | undefined
    >(
        templateviewConfig?.rails?.reduce<
            Record<RBParsedRail['id'], RailLoadingStatus>
        >(
            (acc, { id: railId }) => ({
                ...acc,
                [railId]: RailLoadingStatus.IDLE,
            }),
            {},
        ),
    );
    const [refreshing, setRefreshing] = useState(false);

    const railsPositions = useRef(
        templateviewConfig?.rails?.reduce<
            Record<RBParsedRail['id'], { y: number; height: number }>
        >(
            (acc, { id: railId }) => ({
                ...acc,
                [railId]: { y: 0, height: 0 },
            }),
            {},
        ),
    );

    const galleryExists = useMemo(
        () =>
            !!(
                templateviewConfig &&
                templateviewConfig.rails?.some(
                    (rail) =>
                        rail.properties?.ContentType === ContentType.Gallery,
                )
            ),
        [templateviewConfig],
    );

    const genericEdges: Edge[] = useSafeAreaEdges();
    const edges: Edge[] = useMemo(
        () =>
            galleryExists && !screenReaderEnabled
                ? [...genericEdges, 'top']
                : genericEdges,
        [genericEdges, galleryExists, screenReaderEnabled],
    );
    const areScrollButtonsVisible = useMemo(
        () =>
            isWeb &&
            !window.location.pathname.includes(SCREEN.RECORDINGS.toLowerCase()),
        [screenFocused],
    );

    const isRecordingsPage = useMemo(() => {
        const recordingsTab = tabs.find(
            (tab) => tab.name === RBTemplates.Recordings,
        );

        return id === recordingsTab?.id;
    }, [id, tabs]);

    useHeaderConfig({
        pageScrollPosition,
        screenFocused,
        isRoot: true,
        headerTransparent:
            (!isWeb && galleryExists && !screenReaderEnabled) ||
            (isWeb && galleryExists),
    });

    useEffect(() => {
        const noRailsAfterSuccessfulFetch =
            requestSuccess(templateviewRequestStatus) &&
            !templateviewConfig?.rails.length;
        const everyRailIsNotVisible = templateviewConfig?.rails.every(
            ({ properties }) => !properties.visible,
        );
        const everyRailHasNoData =
            railsLoadingStatus &&
            Object.keys(railsLoadingStatus).every(
                (railId) =>
                    railsLoadingStatus[railId] === RailLoadingStatus.EMPTY,
            );
        const everyRailFailedToLoad =
            railsLoadingStatus &&
            Object.keys(railsLoadingStatus).every(
                (railId) =>
                    railsLoadingStatus[railId] === RailLoadingStatus.FAILURE,
            );

        // If page can't display data because of one of the failure reasons - consider this page as an empty page without any content.
        if (
            requestFailure(mainMenuConfigRequestStatus) ||
            requestFailure(templateviewRequestStatus) ||
            everyRailFailedToLoad
        ) {
            setPageState(PageStatus.FAILED);
            // If page can't display data because there is not enough data to display - consider this page as an empty page without any content.
        } else if (
            !templateviewConfig ||
            noRailsAfterSuccessfulFetch ||
            everyRailIsNotVisible ||
            everyRailHasNoData
        ) {
            setPageState(PageStatus.EMPTY);
        }
    }, [
        templateviewConfig,
        templateviewRequestStatus,
        railsLoadingStatus,
        mainMenuConfigRequestStatus,
    ]);

    const handleSetStripeLoadingStatus = useCallback(
        (stripeId: RBParsedRail['id'], status: RailLoadingStatus) => {
            setRailsLoadingStatus((prevRailsLoadingStatus) => ({
                ...prevRailsLoadingStatus,
                [stripeId]: status,
            }));
        },
        [],
    );

    const handleSetRailToLoad = useCallback((stripeId: string) => {
        setRailsLoadingStatus((prevRailsLoadingStatus) => {
            const stripeLoadingStatus = prevRailsLoadingStatus?.[stripeId];

            if (
                stripeLoadingStatus === RailLoadingStatus.IDLE ||
                stripeLoadingStatus === undefined
            ) {
                return {
                    ...prevRailsLoadingStatus,
                    [stripeId]: RailLoadingStatus.TO_LOAD,
                };
            }

            return prevRailsLoadingStatus;
        });
    }, []);

    const handlePagePositionUpdate = useCallback(
        (position: number, rails: RBParsedRail[], pullToRefresh: boolean) => {
            if (!pullToRefresh) {
                rails.forEach(({ id: stripeId }) => {
                    const stripeScreenPosition =
                        railsPositions.current?.[stripeId]?.y;

                    if (
                        typeof stripeScreenPosition === 'number' &&
                        position >= stripeScreenPosition
                    ) {
                        handleSetRailToLoad(stripeId);
                    }
                });
            }
        },
        [handleSetRailToLoad],
    );

    const debouncedHandlePagePositionUpdate = useRef(
        debounce(handlePagePositionUpdate, 100),
    );

    useAnimatedReaction(
        () => pageScrollPosition.value + windowHeight,
        (currentPagePosition, prevPagePosition) => {
            if (
                templateviewConfig?.rails?.length &&
                typeof prevPagePosition === 'number' &&
                currentPagePosition > prevPagePosition
            ) {
                runOnJS(debouncedHandlePagePositionUpdate.current)(
                    currentPagePosition,
                    templateviewConfig?.rails,
                    refreshing,
                );
            }
        },
        [templateviewConfig?.rails, refreshing],
    );

    const handleSetRailPosition = useCallback(
        (
            stripeId: RBParsedRail['id'],
            layout: LayoutChangeEvent['nativeEvent']['layout'],
        ) => {
            const pagePosition = (pageScrollPosition.value || 0) + windowHeight;
            const prevPositions = railsPositions.current;

            // Edge case for Web only. When Modular page becomes not focused - web app
            // is calling onLayout for every view on the screen, and simply provides layout object with all
            // properties equal to 0, so without this if, such situation would be considered as: stripe
            // tried to load it data, but data is empty, so app will remove this stripe and start loading next one.
            // In result, we will receive the case when all invisible stripes will start loadin data without real need.
            if (
                !(
                    isWeb &&
                    railsLoadingStatus?.[stripeId] !==
                        RailLoadingStatus.EMPTY &&
                    railsLoadingStatus?.[stripeId] !==
                        RailLoadingStatus.FAILURE &&
                    prevPositions?.[stripeId]?.height != null &&
                    prevPositions[stripeId].height > 0 &&
                    layout.height === 0
                )
            ) {
                const updatedPositions = {
                    ...prevPositions,
                    [stripeId]: { y: layout.y, height: layout.height },
                };

                if (
                    prevPositions?.[stripeId]?.height != null &&
                    prevPositions[stripeId].height > 0 &&
                    layout.height === 0
                ) {
                    // If some stripe will be empty or failed to load - it will return null and reset its `y` and `height` values to 0,
                    // in result app must reduce `y` position for all ongoing stripes after the removed one, and call loading state
                    // for the stripes which will appear in the viewport.
                    const removedStripeHeight = prevPositions[stripeId].height;
                    let haveToReducePosition = false;

                    templateviewConfig?.rails?.forEach((rail) => {
                        if (haveToReducePosition) {
                            updatedPositions[rail.id] = {
                                ...updatedPositions[rail.id],
                                y:
                                    updatedPositions[rail.id].y -
                                    removedStripeHeight,
                            };
                        }

                        const stripeScreenPosition =
                            updatedPositions[rail.id].y;

                        if (
                            typeof stripeScreenPosition === 'number' &&
                            pagePosition >= stripeScreenPosition
                        ) {
                            handleSetRailToLoad(rail.id);
                        }

                        if (rail.id === stripeId) {
                            haveToReducePosition = true;
                        }
                    });
                } else {
                    // In case if stripe is rendering for the first time and doesn't have `loadingStatus` set before,
                    // app will set `loading` status for it to immediately start loading the data
                    const stripeScreenPosition = updatedPositions[stripeId].y;

                    if (
                        typeof stripeScreenPosition === 'number' &&
                        pagePosition >= stripeScreenPosition
                    ) {
                        handleSetRailToLoad(stripeId);
                    }
                }

                railsPositions.current = updatedPositions;
            }
        },
        [templateviewConfig?.rails, handleSetRailToLoad, railsLoadingStatus],
    );

    const handlePullToRefresh = useCallback(async () => {
        setRefreshing(true);

        const minRefreshTimeout = new Promise((resolve) => {
            setTimeout(() => {
                resolve(true);
            }, 800);
        });
        const updateTemplatesummary = new Promise((resolve) => {
            dispatch(
                getTemplateview(id, {
                    onSuccess() {
                        resolve(true);
                    },
                    onFailure() {
                        resolve(true);
                    },
                }),
            );
        });

        await Promise.all([minRefreshTimeout, updateTemplatesummary]);

        setRefreshing(false);
        navigation.replace(name, { id });
    }, [id, name, navigation]);

    useEffect(() => {
        const usubscribeFocusListener = navigation.addListener('focus', () => {
            dispatch(
                endSentryTransaction(
                    TEMPLATE_TO_SENTRY_NAVIGATION[id as RootTemplates],
                ),
            );

            if (pageState === PageStatus.RELOAD) {
                setPageState(PageStatus.IDLE);
            }
        });
        const usubscribeBlurListener = navigation.addListener('blur', () => {
            if (
                pageState === PageStatus.FAILED &&
                railsLoadingStatus &&
                Object.keys(railsLoadingStatus).every(
                    (railId) =>
                        railsLoadingStatus[railId] ===
                        RailLoadingStatus.FAILURE,
                )
            ) {
                setPageState(PageStatus.RELOAD);
                setRailsLoadingStatus(undefined);
            }
        });

        return () => {
            usubscribeFocusListener();
            usubscribeBlurListener();
        };
    }, [navigation, railsLoadingStatus, pageState, dispatch, id]);

    useEffect(() => {
        const currentScreenName =
            navigationRef.current?.getCurrentRoute()?.name;

        if (currentScreenName === SCREEN.ROOT) {
            dispatch(
                endSentryTransaction(SENTRY_TRANSACTIONS.MainPageRendering),
            );
        } else {
            dispatch(
                endSentryTransaction(
                    TEMPLATE_TO_SENTRY_RENDERING[id as RootTemplates],
                ),
            );
        }
    }, []);

    if (isNonInteractive && isRecordingsPage) {
        return <Promotional />;
    }

    if (!interactionFinished || pageState === PageStatus.RELOAD) {
        return (
            <SafeAreaView
                edges={genericEdges}
                style={[
                    gStyles.flex,
                    gStyles.centerItems,
                    {
                        backgroundColor: background,
                    },
                ]}
            >
                <Spinner routeKey={key} />
            </SafeAreaView>
        );
    }

    if (pageState === PageStatus.EMPTY || pageState === PageStatus.FAILED) {
        let message =
            pageState === PageStatus.FAILED
                ? t(ERROR_EMPTY_TEMPLATE_RESPONSE)
                : t(EMPTY_CONTENT_PAGES_GLOBAL);

        if (isRecordingsPage) {
            if (!isCapabilityValid(npvrCapability)) {
                message = t(EMPTY_CONTENT_PAGES_NO_RECORDING_CAPABILITY);
            } else if (pageState === PageStatus.EMPTY) {
                message = t(EMPTY_CONTENT_PAGES_RECORDING_BE_ERROR);
            } else if (
                railsLoadingStatus &&
                Object.keys(railsLoadingStatus).every(
                    (railId) =>
                        railsLoadingStatus[railId] ===
                        RailLoadingStatus.FAILURE,
                )
            ) {
                message = t(ALERT_RECORDING_NOT_AVAILABLE_DESCRIPTION);
            }
        }

        return (
            <SafeAreaView
                edges={edges}
                style={[
                    gStyles.flex,
                    gStyles.centerItems,
                    {
                        backgroundColor: background,
                    },
                ]}
                testID={FALLBACK_MESSAGE_ID}
            >
                <Text
                    style={styles.noContent}
                    accessibilityLabel={message}
                    accessibilityRole="text"
                    accessible
                >
                    {message}
                </Text>
            </SafeAreaView>
        );
    }

    return (
        <>
            <SafeAreaView
                edges={edges}
                style={[
                    gStyles.flex,
                    playerVisible && isPhone && styles.hidden,
                ]}
                accessibilityElementsHidden={
                    playerVisible || spotlightPlayerVisible
                }
                importantForAccessibility={
                    playerVisible || spotlightPlayerVisible
                        ? 'no-hide-descendants'
                        : 'auto'
                }
                dataSet={underlayingPlayerModalElementsDataSet}
            >
                <Animated.ScrollView
                    style={styles.bottomGap}
                    scrollEventThrottle={16}
                    showsVerticalScrollIndicator={false}
                    onScroll={onScroll}
                    refreshControl={
                        isWeb ? undefined : (
                            <RefreshControl
                                refreshing={refreshing}
                                onRefresh={handlePullToRefresh}
                            />
                        )
                    }
                    id={`verticalScrollContainer-${key}`}
                    nestedScrollEnabled
                    removeClippedSubviews
                    bounces
                >
                    {templateviewConfig?.rails?.map((rail, index) => (
                        <Stripe
                            key={rail.id}
                            rail={rail}
                            templateId={id}
                            navigation={navigation}
                            loadingStatus={
                                railsLoadingStatus?.[rail.id] ||
                                RailLoadingStatus.IDLE
                            }
                            screenFocused={screenFocused}
                            screenReaderEnabled={screenReaderEnabled}
                            onSetStripeLoadingStatus={
                                handleSetStripeLoadingStatus
                            }
                            onSetStripePosition={handleSetRailPosition}
                            onShowPinDialog={onShowPinDialog}
                            onSetParentalControlCheckedCallback={
                                onSetParentalControlCheckedCallback
                            }
                            index={index}
                        />
                    ))}
                </Animated.ScrollView>
            </SafeAreaView>
            <View>
                <GenericPinDialog
                    modal
                    appState={appState}
                    visible={showPinDialog}
                    onClose={onParentalContolDismiss}
                    onSuccess={onParentalControlChecked}
                />
            </View>
            {areScrollButtonsVisible && (
                <ScrollArrowButtons
                    viewId={`verticalScrollContainer-${key}`}
                    hasGallery={galleryExists}
                />
            )}
        </>
    );
}

export default ModularPage;
